import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";
import { forwardRef } from "react";

import { Spinner } from "@/web/components/primitives/spinner";
import { cn } from "@/web/lib/styling";

const buttonVariants = cva(
  cn(
    "relative inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap enabled:cursor-pointer disabled:cursor-not-allowed",
    "border-solid border-transparent transition-all outline-none [--button-border-width:2px]",
    "[border-width:var(--button-border-width)]",
  ),
  {
    variants: {
      variant: {
        solid: "",
        outline: "",
        ghost: "",
        link: "border-none p-0! underline",
      },
      size: {
        sm: "px-3 py-2 text-xs [&_svg]:size-4",
        md: "px-4 py-3 text-sm [&_svg]:size-5",
        lg: "px-6 py-3.5 text-sm [&_svg]:size-6",
        xl: "px-7 py-4 text-lg [&_svg]:size-7",
        icon: "size-7 p-0 [&_svg]:size-4",
      },
      color: {
        red: "",
        blue: "",
        yellow: "",
        darkBlue: "",
        white: "",
      },
    },
    defaultVariants: {
      variant: "solid",
      size: "md",
      color: "darkBlue",
    },
    compoundVariants: [
      // solid
      {
        variant: "solid",
        color: "darkBlue",
        className: "bg-withus-dark-blue text-withus-pure-white",
      },
      {
        variant: "solid",
        color: "red",
        className: "bg-withus-red text-withus-white",
      },
      {
        variant: "solid",
        color: "blue",
        className: "bg-withus-blue text-withus-white",
      },
      {
        variant: "solid",
        color: "yellow",
        className: "border-withus-blue bg-withus-yellow text-withus-blue",
      },
      {
        variant: "solid",
        color: "white",
        className: "bg-withus-white text-withus-dark-blue",
      },
      // outline
      {
        variant: "outline",
        color: "darkBlue",
        className: "border-withus-dark-blue bg-transparent text-withus-dark-blue",
      },
      {
        variant: "outline",
        color: "red",
        className: "border-withus-red bg-transparent text-withus-red",
      },
      {
        variant: "outline",
        color: "blue",
        className: "border-withus-blue bg-transparent text-withus-blue",
      },
      {
        variant: "outline",
        color: "yellow",
        className: "border-withus-yellow bg-transparent text-withus-yellow",
      },
      {
        variant: "outline",
        color: "white",
        className: "border-withus-white bg-transparent text-withus-white",
      },
    ],
  },
);

const Button = forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      isLoading?: boolean;
    }
>(function Button(
  {
    className,
    variant,
    size,
    color,
    isLoading,
    disabled,
    asChild = false,
    type = "button",
    ...props
  },
  ref,
) {
  const isDisabled = disabled || isLoading;
  const Comp = asChild ? Slot : "button";
  const buttonClassName = cn(
    buttonVariants({ variant, size, color, className }),
    "focus-visible:ring-2 focus-visible:ring-black/20",
    isDisabled && "*:pointer-events-none",
  );
  const hasFullWidth = /\bw-full\b/.test(buttonClassName);

  return (
    <span
      className={cn(
        "inline-flex",
        hasFullWidth && "w-full",
        isDisabled && "cursor-not-allowed",
        disabled && !isLoading && "opacity-50",
      )}
    >
      <Comp
        ref={ref}
        data-slot="button"
        className={buttonClassName}
        type={type}
        disabled={isDisabled}
        {...props}
      >
        <span className={cn("inline-flex items-center gap-2", isLoading && "opacity-0")}>
          {props.children}
        </span>
        {isLoading ? (
          <span className="absolute inset-0 flex items-center justify-center">
            <Spinner className="size-[1em] text-current" />
          </span>
        ) : null}
        <div
          className={cn(
            "pointer-events-none absolute -inset-(--button-border-width) bg-background/0 transition-colors",
            isDisabled && "bg-background/30",
          )}
        />
      </Comp>
    </span>
  );
});

type ButtonProps = React.ComponentProps<typeof Button>;

export { Button, type ButtonProps, buttonVariants };
